import { Box } from '@mui/material';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  FlexCol,
  FlexRow,
  FormSelect,
  NoraButton,
} from '../../../../common/components/v2';
import { Decision, DecisionType } from '../../../../common/constants/decision';
import { Resource } from '../../../../common/constants/resource';
import ElementLocators from '../../../../common/ElementLocators';
import { IDecision } from '../../interfaces/IDecision';
import { DecisionUpdateReasons } from '../decisionUpdateReasons';
import { MIBCodebacks } from '../MIBCodebacks';
import styles from '../styles.module.scss';
import useIneligibleForm, { IneligibleFormData } from './useIneligibleForm';

const decisionUpdateOptions = Object.values(DecisionUpdateReasons).map(
  value => ({ value, label: value }),
);

const decisionOptions = [
  { value: Decision.DECLINED, label: Resource.DECLINE_OPTION },
  { value: Decision.APPROVED, label: Resource.APPROVE_OPTION },
  { value: Decision.POSTPONED, label: Resource.POSTPONE_OPTION },
  { value: Decision.INELIGIBLE, label: Resource.INELIGIBLE_OPTION },
  { value: Decision.WAIVED, label: Resource.WAIVED_OPTION },
];

const reasonsOptions = [
  {
    value: 'No evidence found',
    label: Resource.INELIGIBLE_REASON_NO_EVIDENCE_FOUND,
  },
  {
    value: 'Insufficient evidence',
    label: Resource.INELIGIBLE_REASON_INSUFFICIENT_EVIDENCE,
  },
  {
    value: 'Evidence not recent enough',
    label: Resource.INELIGIBLE_REASON_EVIDENCE_NOT_RECENT_ENOUGH,
  },
];

const IneligibleForm = ({
  editedDecision,
  isUpdate,
  isOpen,
  onSuccess,
  initialFormData,
  onDecisionChanged,
}: {
  editedDecision?: IDecision;
  isUpdate: boolean;
  isOpen: boolean;
  onSuccess: (formData: IneligibleFormData) => void;
  initialFormData?: IneligibleFormData | null;
  onDecisionChanged?: (decision: Decision) => void;
}) => {
  const { handleSubmit, control, clear, reset, watch } = useIneligibleForm({
    isUpdate,
  });

  const isPlanFDecision = editedDecision?.decisionType === DecisionType.PLAN_F;

  const decision = watch('decision');

  useEffect(() => {
    if (isOpen) return;
    clear();
  }, [isOpen, clear]);

  useEffect(() => {
    if (!initialFormData) return;
    reset(initialFormData);
  }, [reset, initialFormData]);

  useEffect(() => {
    onDecisionChanged?.(decision);
  }, [onDecisionChanged, decision]);

  return (
    <Box display="flex" flexDirection="column" gap={2} className={styles.DrawerFormContent}>
      <Box flex={1} display="flex" flexDirection="column" gap={3}>
        {isUpdate && (
          <>
            <FlexRow>
              <FlexCol xs={12}>
                <Controller
                  control={control}
                  name="updateReason"
                  render={({ field, fieldState }) => (
                    <FormSelect
                      required
                      label="Reason for update"
                      options={decisionUpdateOptions}
                      error={fieldState.error?.message}
                      {...field}
                    />
                  )}
                />
              </FlexCol>
            </FlexRow>
            <FlexRow>
              <FlexCol xs={12}>
                <Controller
                  control={control}
                  name="decision"
                  render={({ field, fieldState }) => (
                    <FormSelect
                      required
                      label="Decision"
                      options={decisionOptions}
                      error={fieldState.error?.message}
                      {...field}
                    />
                  )}
                />
              </FlexCol>
            </FlexRow>
          </>
        )}
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="mibCodes"
              render={({ field, fieldState }) => (
                <MIBCodebacks
                  label="MIB codes"
                  error={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
        <FlexRow>
          {isPlanFDecision
            ? 'A Plan F audit could not be successfully completed for this application.'
            : 'A post issue audit could not be successfully completed for this application.'}
        </FlexRow>
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="reason"
              render={({ field, fieldState }) => (
                <FormSelect
                  required
                  options={reasonsOptions}
                  label="Ineligible reason"
                  error={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
      </Box>
      <FlexRow>
        <FlexCol xs={12}>
          <NoraButton
            className={styles.DrawerButton}
            variant="primary"
            name={ElementLocators.LOCATOR_REVIEW_INELIGIBLE_BUTTON}
            onClick={handleSubmit(onSuccess)}
          >
            {isUpdate ? Resource.REVIEW_DECISION : Resource.REVIEW_APPLICATION}
          </NoraButton>
        </FlexCol>
      </FlexRow>
    </Box>
  );
};

export default IneligibleForm;
